import {filter, firstValueFrom, map, Observable, share, Subject} from "rxjs";
import {singleton} from "tsyringe";
import {ICommand} from "../commands/i-command.interface";
import {EventName} from "../events/event-name.enum";
import {IEvent} from "../events/i-event.interface";
import {SystemEvent} from "../events/system.event";
import {ArrayHelper} from "../helpers/array.helper";
import {CommandSendPayload} from "../message-broker/payloads/command-send.payload";

@singleton()
export class EventBus {

    private _eventStore: { eventName: EventName, payload: any }[] = [];

    private _eventPipe = new Subject<IEvent<any>>();

    private _eventPipe$ = this._eventPipe.pipe(
        share()
    );

    public getLastEvents(count: number): { eventName: EventName, payload: any }[] {
        return this._eventStore.slice(0, count);
    }

    public dispatch<T extends any>(eventName: EventName, payload: any = null): void {
        this._eventStore.unshift({payload, eventName});
        this._eventPipe.next(new SystemEvent(eventName, payload))
    }

    public listen<T>(eventName: EventName): Observable<IEvent<T>> {
        return this._eventPipe$.pipe(
            filter(event => event.name === eventName),
            share()
        );
    }

    public listenOnceForCommandSend(command: ICommand<CommandSendPayload>): Promise<boolean> {
        return firstValueFrom(
            this.listen<CommandSendPayload>(EventName.COMMAND_SEND).pipe(
                filter(evt => evt.payload!.command === command),
                map(evt => evt.payload!.success)
            )
        );
    }

    /**
     * The sibling of `listenOnceForCommandSend` that resolves the WHOLE payload
     * rather than only `payload.success`.
     *
     * Same identity filter, so it waits for exactly the one dispatched command
     * it was given — not for whichever command happens to settle next. It exists
     * because a bare boolean cannot distinguish a lock conflict from a crash,
     * and because changing the existing method's return type would touch every
     * one of its callers to serve the few that need the difference.
     */
    public listenOnceForCommandSendResult(command: ICommand<CommandSendPayload>): Promise<CommandSendPayload> {
        return firstValueFrom(
            this.listen<CommandSendPayload>(EventName.COMMAND_SEND).pipe(
                filter(evt => evt.payload!.command === command),
                map(evt => evt.payload!)
            )
        );
    }

    public listenForCommandSendByPattern(pattern: RegExp): Observable<IEvent<CommandSendPayload>> {
        return this.listen<CommandSendPayload>(EventName.COMMAND_SEND).pipe(
            filter(evt => evt.payload
                ? pattern.test(evt.payload.command.name)
                : false
            )
        );
    }

    public listenForCommandSendByTags(...tagLists: Array<string[]>): Observable<IEvent<CommandSendPayload>> {
        return this.listen<CommandSendPayload>(EventName.COMMAND_SEND).pipe(
            filter(
                evt => tagLists
                    .some(
                        tags => ArrayHelper.intersect(
                            evt.payload?.command.tags ?? [], tags
                        ).length === tags.length
                    )
            )
        )
    }
}